import { ReactComponent as HamburgerMenuIcon } from "assets/svg/menu-hamburger.svg";
import { ReactComponent as OrchidLogo } from "assets/svg/orchid-logo.svg";
import { ReactComponent as OutlineMessageCircleLines } from "assets/svg/outline-message-circle-lines.svg";
import { ReactComponent as OutlineDocsFilled } from "assets/svg/outline-document-filled.svg";
// import MessageNotifications from "components/message-notifications";
import { ReactComponent as OutlineChevronDown } from "assets/svg/outline-chevron-down.svg";
import { ReactComponent as OutlineChevronRight } from "assets/svg/outline-chevron-right-16.svg";
import { ReactComponent as HomeIcon } from "assets/svg/outline-home-1.svg";
import User from "components/after-login-navbar/components/user";
import logoutUser from "components/after-login-navbar/utils/logoutUser";
import ClientSelect from "components/client-select/ClientSelect";
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import { GENERAL_ROUTES } from "constants/generalRoutes";
import { useClientsContext } from "contexts/ClientsContext";
import { useUser } from "contexts/UserContext";
import { useSubscription } from "contexts/subscription-context";
import useOnClickOutside from "hooks/useOnClickOutside";
import { useEffect, useRef, useState } from "react";
import { Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { NavLink, useNavigate } from "react-router-dom";
import { AmplitudePageName, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import getClientDisplayName from "utils/getClientDisplayName";
import styles from "./AINavbar.module.scss";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "common-navigation";

const AINavbar = () => {
  const navigate = useNavigate();

  const { user } = useUser();
  const { canAccessPlatform } = useSubscription();
  const { individuals, couples, initializeClients } = useClientsContext();

  const [shouldShowClientDropdown, setShouldShowClientDropdown] =
    useState(false);

  const clientSelectWrapperRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(clientSelectWrapperRef, () =>
    setShouldShowClientDropdown(false),
  );

  const clientPageMatch = window.location.pathname.match(/\/client\/(.*)$/);
  const currentClientId = clientPageMatch ? clientPageMatch[1] : undefined;
  const currentClient = [...individuals, ...couples].find(
    (client) => client.patient.orchid_user_id === currentClientId,
  );
  const currentClientDisplayName = currentClient
    ? getClientDisplayName(currentClient)
    : "Client";

  useEffect(() => {
    initializeClients();
  }, [initializeClients]);

  return (
    <nav className={styles["container"]}>
      <NavLink className={styles["orchid-ai"]} to="/pro-dashboard/home">
        <OrchidLogo height={20} aria-hidden={"true"} />
        <div className={styles["orchid-ai__title"]}>AI</div>
      </NavLink>

      <NavLink to="/pro-dashboard/home" className={styles["top-level-link"]}>
        <HomeIcon />
      </NavLink>
      <NavLink
        to="/pro-dashboard/notes-and-forms"
        className={styles["top-level-link"]}
      >
        <OutlineDocsFilled />
      </NavLink>

      {currentClientId && (
        <div className={styles["client-breadcrumb"]}>
          <OutlineChevronRight />
          <div
            className={getClasses(
              styles["client-breadcrumb-wrapper"],
              shouldShowClientDropdown &&
                styles["client-breadcrumb-wrapper--selected"],
            )}
            onClick={() => {
              setShouldShowClientDropdown((previous) => !previous);
            }}
          >
            <div className={styles["client-breadcrumb-content"]}>
              <NavLink
                to={`/pro-dashboard/client/${currentClientId}`}
                className={styles["top-level-link"]}
              >
                {currentClientDisplayName}
              </NavLink>
              <OutlineChevronDown width={16} height={16} />
            </div>
          </div>
        </div>
      )}

      {shouldShowClientDropdown && (
        <div
          className={styles["client-select-wrapper"]}
          ref={clientSelectWrapperRef}
        >
          <ClientSelect
            onSelect={(client) => {
              setShouldShowClientDropdown(false);
              navigate(
                `/pro-dashboard/client/${client.patient.orchid_user_id}`,
              );
            }}
          />
        </div>
      )}

      <div className="flex-grow-1" />

      <NavLink
        className={styles["top-level-link"]}
        to="/pro-dashboard/messages"
        aria-label={"messages"}
        onClick={() => {
          track(AMPLITUDE_PAGE_NAME, "messages-link", "click");
        }}
      >
        <OverlayTrigger
          placement="bottom"
          overlay={<Tooltip id="button-tooltip">Messages</Tooltip>}
        >
          <div aria-hidden="true">
            <OutlineMessageCircleLines />
            {/* <MessageNotifications /> */}
          </div>
        </OverlayTrigger>
      </NavLink>

      <DropdownToggle title={<User />} Caret={<HamburgerMenuIcon />}>
        <Dropdown.Item
          as={NavLink}
          to={GENERAL_ROUTES.professionalSearch}
          onClick={() =>
            track(AMPLITUDE_PAGE_NAME, "find-a-professional-link", "click")
          }
        >
          Find a professional
        </Dropdown.Item>
        <Dropdown.Item
          as={NavLink}
          to={GENERAL_ROUTES.groupSession}
          onClick={() =>
            track(AMPLITUDE_PAGE_NAME, "find-a-support-group-link", "click")
          }
        >
          Find a support group
        </Dropdown.Item>

        <Dropdown.Item
          as={NavLink}
          to={GENERAL_ROUTES.pricing}
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "pricing-link", "click");
          }}
        >
          Pricing
        </Dropdown.Item>
        <Dropdown.Item
          as={NavLink}
          to={GENERAL_ROUTES.blogs}
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "blog-link", "click");
          }}
        >
          Blog
        </Dropdown.Item>
        <Dropdown.Divider />

        {canAccessPlatform(user) && (
          <>
            <Dropdown.Item
              as={NavLink}
              to="/pro-dashboard/settings/my-profile"
              onClick={() => {
                track(AMPLITUDE_PAGE_NAME, "settings-link", "click");
              }}
            >
              Settings
            </Dropdown.Item>
            <Dropdown.Item
              href="https://help.orchid.exchange/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                track(AMPLITUDE_PAGE_NAME, "support-link", "click");
              }}
            >
              Support
            </Dropdown.Item>
            <Dropdown.Item
              as={NavLink}
              to="/pro-dashboard/policies"
              onClick={() => {
                track(AMPLITUDE_PAGE_NAME, "policies-link", "click");
              }}
            >
              Policies
            </Dropdown.Item>
          </>
        )}

        <Dropdown.Item
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "blog-link", "click");
            logoutUser(user);
          }}
        >
          Log out
        </Dropdown.Item>
      </DropdownToggle>
    </nav>
  );
};

export default AINavbar;
